import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Users, Search, Check, X } from "lucide-react";
import { getSport } from "@/lib/sports";

/**
 * Premium team filter — replaces the chips cloud with a clean trigger
 * that opens a Sheet with search + sport-grouped clubs.
 *
 * Props:
 *  - clubs: [{ name, list, accent, logo, sport? }]  // sport is computed below if missing
 *  - players: full players array (used to derive the dominant sport per club)
 *  - activeClub: current selected club (or "__all__")
 *  - onChange: (clubName) => void
 */
export default function TeamFilter({ clubs, players, activeClub, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Derive the dominant sport for each club so we can group properly
  const enrichedClubs = useMemo(() => {
    return clubs.map((c) => {
      const sportCount = {};
      players.forEach((p) => {
        if ((p.club_name?.trim() || "Sans équipe") === c.name) {
          sportCount[p.sport] = (sportCount[p.sport] || 0) + 1;
        }
      });
      const sport =
        Object.entries(sportCount).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        "football";
      return { ...c, sport };
    });
  }, [clubs, players]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return enrichedClubs;
    return enrichedClubs.filter((c) => c.name.toLowerCase().includes(term));
  }, [enrichedClubs, q]);

  const groupedBySport = useMemo(() => {
    const out = {};
    filtered.forEach((c) => {
      if (!out[c.sport]) out[c.sport] = [];
      out[c.sport].push(c);
    });
    return out;
  }, [filtered]);

  const total = clubs.reduce((acc, c) => acc + c.list.length, 0);
  const activeClubObj =
    activeClub === "__all__"
      ? null
      : enrichedClubs.find((c) => c.name === activeClub);

  const select = (name) => {
    onChange(name);
    setOpen(false);
    setQ("");
  };

  return (
    <div
      className="inline-flex items-center gap-2 flex-wrap"
      data-testid="team-filter"
    >
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            data-testid="team-filter-trigger"
            className="inline-flex items-center gap-2.5 h-10 px-4 rounded-full bg-white/[0.04] border border-white/15 text-white hover:border-white/30 hover:bg-white/[0.08] transition-all"
          >
            <Users className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold">
              {activeClubObj ? "Équipe" : "Toutes les équipes"}
            </span>
            <span
              className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-md bg-white/10 text-zinc-300"
              data-testid="team-filter-count"
            >
              {activeClubObj ? activeClubObj.list.length : total}
            </span>
          </button>
        </SheetTrigger>

        <SheetContent
          side="right"
          className="bg-[#0A0B0E] border-l border-white/10 text-white p-0 w-full sm:max-w-md"
          data-testid="team-filter-sheet"
        >
          <SheetHeader className="px-5 pt-6 pb-3 border-b border-white/10">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-1.5">
              Laser Tech Création
            </div>
            <SheetTitle className="font-display text-2xl font-black uppercase tracking-tight">
              Filtrer par équipe
            </SheetTitle>
          </SheetHeader>

          {/* Search */}
          <div className="px-5 pt-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une équipe…"
                data-testid="team-filter-search"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
              />
            </div>
          </div>

          {/* "All teams" reset */}
          <div className="px-5 pb-2 pt-1">
            <button
              type="button"
              onClick={() => select("__all__")}
              data-testid="team-filter-all"
              className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-white/[0.05] transition-colors"
              style={{
                background: activeClub === "__all__" ? "rgba(255,255,255,0.06)" : "transparent",
                border: activeClub === "__all__" ? "1px solid rgba(255,255,255,0.2)" : "1px solid transparent",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.05] border border-white/10">
                  <Users className="w-3.5 h-3.5 text-zinc-300" />
                </div>
                <span className="font-display text-sm font-bold uppercase tracking-tight">
                  Toutes les équipes
                </span>
              </div>
              <span className="flex items-center gap-2">
                <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
                  {total}
                </span>
                {activeClub === "__all__" && (
                  <Check className="w-4 h-4 text-white" />
                )}
              </span>
            </button>
          </div>

          {/* Grouped by sport */}
          <div className="overflow-y-auto max-h-[calc(100vh-220px)] pb-8">
            {Object.keys(groupedBySport).length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-zinc-500 font-body">
                Aucune équipe pour «&nbsp;{q}&nbsp;»
              </div>
            )}
            {Object.entries(groupedBySport).map(([sportId, list]) => {
              const sport = getSport(sportId);
              return (
                <div key={sportId} className="px-5 pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                      {sport.label} · {list.length}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {list.map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => select(c.name)}
                        data-testid={`team-filter-item-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                        className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-white/[0.05] transition-colors"
                        style={{
                          background: activeClub === c.name ? "rgba(255,255,255,0.06)" : "transparent",
                          border: activeClub === c.name ? "1px solid rgba(255,255,255,0.2)" : "1px solid transparent",
                        }}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-white/[0.05] border border-white/10">
                            {c.logo ? (
                              <img
                                src={c.logo}
                                alt=""
                                className="w-full h-full object-contain p-0.5"
                              />
                            ) : (
                              <span className="font-display font-black text-xs text-zinc-300">
                                {c.name[0]}
                              </span>
                            )}
                          </div>
                          <span className="font-body text-sm text-white truncate text-left">
                            {c.name}
                          </span>
                        </div>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
                            {c.list.length}
                          </span>
                          {activeClub === c.name && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Active filter pill */}
      {activeClubObj && (
        <button
          type="button"
          onClick={() => onChange("__all__")}
          data-testid="team-filter-clear"
          className="inline-flex items-center gap-2 h-10 px-3 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all bg-white/[0.06] border border-white/20 text-white hover:bg-white/[0.10]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
          {activeClubObj.name}
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
